<template>
  <div>
    <b-alert variant="warning" :show="userAlreadyExists">
      There is already an existing user for this Asurite ID. Please click
      <router-link :to="{ name: 'user', params: { asuriteId: form.selectedUser.asuriteId } }" class="alert-link"
        v-if="form.selectedUser">here</router-link> to view this user's page.
    </b-alert>

    <h3>New User</h3>
    <router-link :to="{ name: 'users' }" class="return-link">Return to Users List</router-link>

    <b-form @submit.prevent="submitUser">
      <b-container class="user-main-container">
        <b-row>
          <b-col sm="3">
            <span class="user-detail-label">Basic Information</span>
            <b-row class="detail-row">
              <b-col sm="4" class="align-right">
                <label for="asurite-select"
                  :class="{ 'user-detail-label': true, 'invalid-label': $v.form.selectedUser.$error }">Asurite
                  ID:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <autocomplete ref="autocomplete" :source="apiEndpoint" credentials="include" resultsValue="asuriteId"
                  resultsDisplay="asuriteId" @selected="changeAsuriteId" @clear="clearUser"
                  :request-headers="requestHeaders" id="asurite-select" :inputClass="{
                    'form-control': true,
                    'text-input': true,
                    'autocomplete-input': true,
                    'is-invalid': $v.form.selectedUser.$error
                  }">
                </autocomplete>
                <span class="span-feedback invalid-feedback" v-if="$v.form.selectedUser.$error">Please select an Asurite
                  ID.</span>
              </b-col>
            </b-row>
            <b-row>
              <b-col sm="4" class="align-right">
                <label for="first-name"
                  :class="{ 'user-detail-label': true, 'invalid-label': $v.form.firstName.$error }">First Name:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <b-form-input id="first-name" v-model="form.firstName"
                  :class="{ 'text-input': true, 'is-invalid': $v.form.firstName.$error }"></b-form-input>
                <span class="span-feedback invalid-feedback" v-if="$v.form.firstName.$error">Please enter a first
                  name.</span>
              </b-col>
            </b-row>
            <b-row>
              <b-col sm="4" class="align-right">
                <label for="last-name"
                  :class="{ 'user-detail-label': true, 'invalid-label': $v.form.lastName.$error }">Last Name:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <b-form-input id="last-name" v-model="form.lastName"
                  :class="{ 'text-input': true, 'is-invalid': $v.form.lastName.$error }"></b-form-input>
                <span class="span-feedback invalid-feedback" v-if="$v.form.lastName.$error">Please enter a last
                  name.</span>
              </b-col>
            </b-row>
            <b-row>
              <b-col sm="4" class="align-right">
                <label for="user-email"
                  :class="{ 'user-detail-label': true, 'invalid-label': $v.form.email.$error }">Email:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <b-form-input id="user-email" v-model="form.email"
                  :class="{ 'text-input': true, 'is-invalid': $v.form.email.$error }" type="email"></b-form-input>
                <span class="span-feedback invalid-feedback" v-if="$v.form.email.$error">Please enter a valid email
                  address.</span>
              </b-col>
            </b-row>
          </b-col>
          <b-col sm="3">
            <span class="user-detail-label">Roles</span>
            <div v-for="role in roles" :key="role.roleId">
              <b-badge :class="{ 'clickable-badge': true, 'unselected-badge': !hasRole(role) }"
                @click="toggleRoleSelection(role)">
                {{ role.name }} <font-awesome-icon :icon="hasRole(role) ? 'times' : 'plus'" />
              </b-badge>
            </div>
          </b-col>
          <b-col sm="3">
            <span class="user-detail-label">Colleges</span>
            <div v-for="college in colleges" :key="college.collegeId">
              <b-badge :class="{ 'clickable-badge': true, 'unselected-badge': !hasCollege(college) }"
                @click="toggleCollegeSelection(college)">
                {{ college.name }} <font-awesome-icon :icon="hasCollege(college) ? 'times' : 'plus'" />
              </b-badge>
            </div>
          </b-col>
          <b-col sm="3">
            <span class="user-detail-label">Academic Plans</span>
            <div>
              <b-button size="sm" v-if="academicPlans.length > 0 && academicPlans.length > form.academicPlans.length"
                @click="selectAllAcademicPlans()">
                <font-awesome-icon icon="plus" /> Select All
              </b-button>
              <b-button size="sm" v-if="academicPlans.length > 0 && academicPlans.length == form.academicPlans.length"
                @click="deselectAllAcademicPlans()">
                <font-awesome-icon icon="times" /> Deselect All
              </b-button>
            </div>
            <b-input-group class="filter-group" v-if="form.colleges.length > 0">
              <b-input-group-prepend is-text>
                <font-awesome-icon icon="search" />
              </b-input-group-prepend>
              <b-form-input v-model="filter" placeholder="Search"></b-form-input>
              <b-input-group-append>
                <b-button @click="filter = null">Clear</b-button>
              </b-input-group-append>
            </b-input-group>
            <div v-for="college in form.colleges" :key="college.collegeId">
              <p class="college-header">{{ college.name }}</p>
              <div v-for="academicPlan in academicPlansByCollege[college.collegeId]" :key="academicPlan.academicPlanId">
                <b-badge :class="{ 'clickable-badge': true, 'unselected-badge': !hasAcademicPlan(academicPlan) }"
                  @click="toggleAcademicPlanSelection(academicPlan)">
                  {{ academicPlan.description }} <font-awesome-icon
                    :icon="hasAcademicPlan(academicPlan) ? 'times' : 'plus'" />
                </b-badge>
              </div>
            </div>
          </b-col>
        </b-row>

        <b-button size="sm" type="submit" :disabled="userAlreadyExists" class="new-user-button"><font-awesome-icon
            icon="check" /> Save user</b-button>
        <b-button size="sm" :to="{ name: 'users' }" class="new-user-button"><font-awesome-icon icon="times" />
          Cancel</b-button>
      </b-container>
    </b-form>
  </div>
</template>

<script>
import { required, email } from "vuelidate/lib/validators";
import toastr from "toastr";
import usersApi from "../../api/users";

export default {
  data() {
    return {
      form: {
        selectedUser: null,
        firstName: null,
        lastName: null,
        email: null,
        roles: [],
        colleges: [],
        academicPlans: []
      },
      userAlreadyExists: false,
      autocompleteResults: [],
      requestHeaders: { "X-Requested-With": "XMLHttpRequest" }
    };
  },

  props: {
    roles: {
      type: Array,
      required: true
    },

    colleges: {
      type: Array,
      required: true
    }
  },

  computed: {
    academicPlans: function () {
      var that = this;
      var academicPlanList = [];
      _.forEach(this.form.colleges, function (college) {
        _.forEach(college.academicPlans, function (academicPlan) {
          // filtering
          if (
            !that.filter ||
            academicPlan.description
              .toLowerCase()
              .includes(that.filter.toLowerCase())
          ) {
            academicPlanList.push(academicPlan);
          }
        });
      });
      return academicPlanList;
    },

    academicPlansByCollege: function () {
      // group by collegeId
      var groupedAcademicPlans = _.groupBy(this.academicPlans, "collegeId");

      // alphabetize academicPlans
      _.forEach(groupedAcademicPlans, function (group, collegeId) {
        group = _.sortBy(group, ["description"]);
        groupedAcademicPlans[collegeId] = group;
      });

      return groupedAcademicPlans;
    },

    apiEndpoint: function () {
      return process.env.VUE_APP_BASE_URI + "/api/Users/FindLDAP?search=";
    }
  },

  validations: {
    form: {
      selectedUser: {
        required
      },
      firstName: {
        required
      },
      lastName: {
        required
      },
      email: {
        required,
        email
      }
    }
  },

  methods: {
    // function to run when an asuriteId has been chosen from the dropdown
    // not only sets the asuriteId, but autopopulates name and email
    changeAsuriteId: function (autoselectObject) {
      var that = this;
      this.form.selectedUser = autoselectObject.selectedObject;
      this.form.firstName = this.form.selectedUser.firstName;
      this.form.lastName = this.form.selectedUser.lastName;
      this.form.email = this.form.selectedUser.email;

      // check if this user already exists
      usersApi
        .getUserExists(this.form.selectedUser.asuriteId)
        .then(function (response) {
          that.userAlreadyExists = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    // function called when asurite ID autoselect is cleared
    clearUser: function () {
      this.form.selectedUser = null;
      this.userAlreadyExists = false;
    },

    // check if this role has already been added to the user
    hasRole: function (role) {
      return _.find(this.form.roles, function (addedRole) {
        return addedRole.roleId === role.roleId && addedRole.app === role.app;
      });
    },

    hasCollege: function (college) {
      return _.find(this.form.colleges, function (addedCollege) {
        return addedCollege.collegeId === college.collegeId;
      });
    },

    hasAcademicPlan: function (academicPlan) {
      return _.find(this.form.academicPlans, function (addedAcademicPlan) {
        return addedAcademicPlan.academicPlanId === academicPlan.academicPlanId;
      });
    },

    toggleRoleSelection: function (role) {
      if (this.hasRole(role)) {
        this.form.roles = _.reject(this.form.roles, function (addedRole) {
          return addedRole.roleId === role.roleId && addedRole.app === role.app;
        });
      } else {
        this.form.roles.push(role);
      }
    },

    toggleCollegeSelection: function (college) {
      var that = this;
      if (this.hasCollege(college)) {
        this.form.colleges = _.reject(this.form.colleges, function (
          addedCollege
        ) {
          return addedCollege.collegeId === college.collegeId;
        });
        // remove academic plans for this college as well
        _.forEach(this.form.academicPlans, function (academicPlan) {
          if (academicPlan.collegeId === college.collegeId) {
            that.toggleAcademicPlanSelection(academicPlan);
          }
        });
      } else {
        this.form.colleges.push(college);
        // make sure colleges are sorted in same order that they are shown on the form
        // this ensures that academic plans will also be shown in the same order as the colleges on the form
        this.form.colleges = _.sortBy(this.form.colleges, ["collegeId"]);
      }
    },

    toggleAcademicPlanSelection: function (academicPlan) {
      if (this.hasAcademicPlan(academicPlan)) {
        this.form.academicPlans = _.reject(this.form.academicPlans, function (
          addedAcademicPlan
        ) {
          return (
            addedAcademicPlan.academicPlanId === academicPlan.academicPlanId
          );
        });
      } else {
        this.form.academicPlans.push(academicPlan);
      }
    },

    selectAllAcademicPlans: function () {
      var that = this;
      _.forEach(this.academicPlans, function (academicPlan) {
        if (!that.hasAcademicPlan(academicPlan)) {
          that.form.academicPlans.push(academicPlan);
        }
      });
    },

    deselectAllAcademicPlans: function () {
      this.form.academicPlans = [];
    },

    submitUser: function () {
      var that = this;
      this.$v.$touch();

      // only submit if the form is valid
      if (!this.$v.$invalid) {
        // user object
        var newUser = {
          asuriteId: this.form.selectedUser.asuriteId,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          userRoles: [],
          userColleges: [],
          userAcademicPlans: []
        };

        // add userRoles
        _.forEach(this.form.roles, function (role) {
          var newUserRole = {
            asuriteId: newUser.asuriteId,
            roleId: role.roleId,
            role: {
              app: role.app,
              name: role.name
            }
          };
          newUser.userRoles.push(newUserRole);
        });

        // add userColleges
        _.forEach(this.form.colleges, function (college) {
          var newUserCollege = {
            asuriteId: newUser.asuriteId,
            collegeId: college.collegeId
          };
          newUser.userColleges.push(newUserCollege);
        });

        // add academicPlans
        _.forEach(this.form.academicPlans, function (academicPlan) {
          var newUserAcademicPlan = {
            asuriteId: newUser.asuriteId,
            academicPlanId: academicPlan.academicPlanId
          };
          newUser.userAcademicPlans.push(newUserAcademicPlan);
        });

        usersApi
          .createUser(newUser)
          .then(function () {
            toastr.options = {
              closeButton: true,
              debug: false,
              newestOnTop: false,
              progressBar: false,
              positionClass: "toast-top-right",
              preventDuplicates: false,
              onclick: null,
              showDuration: "300",
              hideDuration: "0",
              timeOut: "5000",
              extendedTimeOut: "1000",
              showEasing: "swing",
              hideEasing: "linear",
              showMethod: "fadeIn",
              hideMethod: "fadeOut"
            };

            // Toastr Message
            toastr.success(
              newUser.firstName +
              " " +
              newUser.lastName +
              " has been added as a user.",
              "Success"
            );

            that.$router.push({
              name: "user",
              params: { asuriteId: newUser.asuriteId }
            });
          })
          .catch(function (error) {
            console.log(error);
          });
      }
    }
  }
};
</script>

<style scoped>
.container {
  max-width: 100%;
  text-align: center;
}

.align-right {
  text-align: right;
}

.align-left {
  text-align: left;
}

.invalid-label {
  color: #dc3545;
}

.user-detail-label {
  font-weight: bold;
  line-height: 38px;
}

.clickable-badge {
  cursor: pointer;
}

.unselected-badge {
  opacity: 0.5;
}

.college-header {
  text-decoration: underline;
  margin-bottom: 0;
  font-weight: bold;
}

.filter-group {
  margin: 10px 0 15px 0;
}

.new-user-button {
  margin-left: 2px;
  margin-right: 2px;
}
</style>
